import {
  AutocompleteInputChangeReason,
  Box,
  CircularProgress,
  createFilterOptions,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { GridSortDirection } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { LabeledAutoComplete } from "../../../UI/LabeledAutoComplete";
import SupplierService from "../../../../services/supplier.service";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useServices } from "../../../../contexts/ServicesContext/projectservices.context";

const INITIAL_STATE = {
  page: 0,
  pageSize: 20,
  order: "asc" as GridSortDirection,
  sortField: "name",
  search: "",
  origin: "",
};

type PropTypes = {
  onSelectSupplier(value: string, field: string): void;
  provider_id: string;
  title: string;
  disabled?: boolean;
  isBudget?: boolean;
};

const SupplierAutoComplete: React.FC<PropTypes> = ({
  onSelectSupplier,
  provider_id,
  disabled,
  isBudget,
}: PropTypes) => {
  const [inputSupplierValue, setInputSupplerValue] = useState("");
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  //   const { activeBudget, activeCalcOption } = useBudget();
  const { activeService, activeCalcOption } = useServices();

  const parsedProviderId = useMemo(() => {
    const providerIdSplit = provider_id.split("-");
    if (providerIdSplit.length > 1) {
      return providerIdSplit[0];
    } else {
      return provider_id;
    }
  }, [provider_id]);

  const { data: provider } = useQuery({
    queryKey: ["provider", parsedProviderId],
    queryFn: () => {
      return SupplierService.getProviders({ id: parsedProviderId });
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: false, // Disable refetch on component mount
    cacheTime: 1000 * 60 * 10, // Data stays in cache for 10 minutes
  });
  const [supplierAutoCompleteSearch, setSupplierAutoCompleteSearch] =
    useState<any>(INITIAL_STATE);
  const { isLoading, data } = useQuery({
    queryKey: [
      "providers_search",
      supplierAutoCompleteSearch,
      inputSupplierValue,
    ],
    queryFn: () =>
      SupplierService.search({
        ...supplierAutoCompleteSearch,
        search: inputSupplierValue,
      }),
  });

  const [supplierValue, setSupplierValue] = useState<any>(null);
  function supplierChangeHandler(
    e: SyntheticEvent<Element, Event>,
    newValue: any | null
  ) {
    if (!!isBudget) {
      const hasTheSameForCalcOption = activeService.supplierList.find(
        (el) =>
          el.supplierId === newValue?._id && el.calcOption === activeCalcOption
      );
      const repeatedTimes = activeService.supplierList.filter(
        (el) =>
          el.supplierId?.includes(newValue?._id) &&
          el.calcOption === activeCalcOption
      ).length;

      // Permite duplicatas para fornecedores "a definir" (temporários)
      const isTemporarySupplier =
        newValue?.cnpj_cpf?.includes("tbd") ||
        newValue?.name?.toUpperCase().includes("A DEFINIR");

      if (isTemporarySupplier) {
        setSupplierValue(newValue);
        onSelectSupplier(
          newValue?._id + `-${activeCalcOption}-${repeatedTimes}` || "",
          isBudget ? newValue?.name : "provider_id"
        );
        return;
      }
      if (hasTheSameForCalcOption) {
        setSupplierValue(null);
        setInputSupplerValue("");
        return toast.warn(
          "Fornecedor duplicado para o tipo de cálculo ativo, use o existente"
        );
      }
    }
    setSupplierValue(newValue);
    onSelectSupplier(
      newValue?._id || "",
      isBudget ? newValue?.name : "provider_id"
    );
  }
  function supplierInputChangeHandler(
    e: SyntheticEvent<Element, Event>,
    newValue: string,
    reason: AutocompleteInputChangeReason
  ) {
    // #MMV-2527: "reset" é o MUI preenchendo o input com
    // getOptionLabel(selecionado) (a razão social | nome fantasia | CNPJ
    // inteira) assim que uma opção é escolhida — não é o usuário buscando.
    // Sem esse guard, reabrir o campo mostrava esse texto cru selecionado
    // e a busca (usando ele como termo) não batia com nada ("No options").
    if (reason === "reset") return;
    setInputSupplerValue(newValue);
  }

  useEffect(() => {
    if (provider_id === "" && !provider_id) {
      setInputSupplerValue("");
      setSupplierValue(null);
    }
  }, [provider_id]);

  useEffect(() => {
    if (!!provider && provider[0]?._id) {
      setSupplierValue(provider[0]);
      setInputSupplerValue(provider[0].name);
    } else {
      setInputSupplerValue("");
      setSupplierValue(null);
    }
  }, [provider]);

  const filterOptions = createFilterOptions({
    stringify: (option: any) => {
      const normalizeDigits = (value: any) =>
        String(value ?? "").replace(/[^\d]/g, "");

      return (
        option.name +
        option.nickname +
        option.tax_id +
        option.cnpj_cpf +
        normalizeDigits(option.tax_id) +
        normalizeDigits(option.cnpj_cpf)
      );
    },
  });

  const getOptionLabel = (option: any) => {
    const isTBD = option.cnpj_cpf?.includes("tbd");
    if (isTBD) return option?.name;
    return `${option.name} | ${option.nickname} | ${option.cnpj_cpf}` || "";
  };

  // #MMV-2527: mesma linha secundária (Nome Fantasia | CNPJ) usada tanto na
  // lista quanto no campo já selecionado, pra manter os dois em sincronia.
  const describeSecondaryLine = (option: any) =>
    [option.nickname, option.cnpj_cpf].filter(Boolean).join(" | ");

  const escapeRegExp = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Destaca (negrito + azul) o trecho do texto pesquisado dentro do texto
  // exibido, igual à referência visual do card.
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    const trimmedQuery = query.trim();
    if (!text || !trimmedQuery) return text;
    const regex = new RegExp(`(${escapeRegExp(trimmedQuery)})`, "gi");
    const parts = text.split(regex);
    if (parts.length === 1) return text;
    return parts.map((part, index) =>
      index % 2 === 1 ? (
        <Box
          key={index}
          component="span"
          sx={{ color: "#1361A4", fontWeight: 700 }}
        >
          {part}
        </Box>
      ) : (
        part
      )
    );
  };

  // Razão social em destaque na linha de cima, nome fantasia e CNPJ
  // separados por "|" na linha de baixo — mesmo padrão em toda listagem de
  // fornecedor (aberta pela primeira vez ou reaberta pra trocar seleção).
  // Trecho pesquisado sai realçado nas duas linhas.
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any
  ) => {
    const isTBD = option.cnpj_cpf?.includes("tbd");
    const secondaryLine = describeSecondaryLine(option);

    return (
      <Box
        component="li"
        {...props}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start !important",
          gap: "3px",
          py: "10px !important",
        }}
      >
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: "#1A1F27" }}>
          {highlightMatch(option.name, inputSupplierValue)}
        </Typography>
        {!isTBD && secondaryLine && (
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            {highlightMatch(secondaryLine, inputSupplierValue)}
          </Typography>
        )}
      </Box>
    );
  };

  // #MMV-2527: com o campo fechado e um fornecedor selecionado, mostra o
  // mesmo padrão de 2 linhas da lista (razão social em negrito + nome
  // fantasia | CNPJ) em vez do texto corrido que o <input> nativo permitiria.
  // A camada de exibição fica por cima, com pointer-events desligado, pra
  // cliques/foco continuarem indo pro input de verdade por baixo.
  const isTBDSelected = supplierValue?.cnpj_cpf?.includes("tbd");
  const selectedSecondaryLine = supplierValue
    ? describeSecondaryLine(supplierValue)
    : "";
  const showRichSelectedDisplay =
    !isAutocompleteOpen &&
    !!supplierValue &&
    !isTBDSelected &&
    !!selectedSecondaryLine;

  // #MMV-2527: desabilitado nunca abre — em vez de um Autocomplete fingindo
  // ser texto estático (lutando contra chevron/contorno/-webkit-text-fill-color
  // do MUI), é só um campo de texto mesmo: mais simples e sem as disputas de
  // especificidade CSS que isso trazia.
  if (disabled) {
    return (
      <Box sx={{ width: "100%" }}>
        <InputLabel style={{ fontSize: "13px" }}>Fornecedor</InputLabel>
        <Box
          sx={{
            width: "100%",
            // Mesma altura do input "size=small" de sempre (40px) — só
            // porque agora tem 2 linhas não pode virar um select gigante.
            height: 40,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: "14px",
            borderRadius: "4px",
            border: "1px solid rgba(0, 0, 0, 0.23)",
            backgroundColor: "#F3F4F6",
          }}
        >
          {supplierValue ? (
            <>
              <Typography
                noWrap
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#6B7280",
                  lineHeight: 1.15,
                }}
              >
                {supplierValue.name}
              </Typography>
              {!isTBDSelected && selectedSecondaryLine && (
                <Typography
                  noWrap
                  sx={{ fontSize: 11, color: "#9CA3AF", lineHeight: 1.15 }}
                >
                  {selectedSecondaryLine}
                </Typography>
              )}
            </>
          ) : (
            <Typography noWrap sx={{ fontSize: 13, color: "#9CA3AF" }}>
              Selecione um fornecedor
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      <LabeledAutoComplete
        filterOptions={filterOptions}
        options={data?.providers || []}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        size="small"
        inputValue={inputSupplierValue}
        value={supplierValue}
        onInputChange={supplierInputChangeHandler}
        onChange={supplierChangeHandler}
        onOpen={() => {
          setIsAutocompleteOpen(true);
          // Reabrir com algo já selecionado: começa a lista limpa (ordem
          // alfabética completa) em vez de filtrar pelo texto cru que
          // sobrou da seleção anterior.
          if (supplierValue) setInputSupplerValue("");
        }}
        onClose={() => setIsAutocompleteOpen(false)}
        label={`Fornecedor`}
        renderInput={(params) => (
          // Wrapper relativo escopado só à caixa do input (não ao label
          // acima) — é o que faz a camada de exibição de baixo alinhar
          // certo com a borda em vez de flutuar a partir do topo do label.
          <Box sx={{ position: "relative", width: "100%" }}>
            <TextField
              placeholder="Selecione um fornecedor"
              {...params}
              fullWidth
              style={{ background: "#FFF" }}
              sx={
                showRichSelectedDisplay
                  ? {
                      "& .MuiOutlinedInput-root": {
                        minHeight: 46,
                        py: "3px !important",
                      },
                      "& .MuiAutocomplete-input": {
                        color: "transparent",
                        caretColor: "transparent",
                      },
                    }
                  : undefined
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
            {showRichSelectedDisplay && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "1px",
                  pl: "14px",
                  pr: "40px",
                  pointerEvents: "none",
                  overflow: "hidden",
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "#1A1F27",
                    lineHeight: 1.3,
                  }}
                >
                  {supplierValue.name}
                </Typography>
                <Typography
                  noWrap
                  sx={{ fontSize: 12, color: "#6B7280", lineHeight: 1.3 }}
                >
                  {selectedSecondaryLine}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      />
    </Box>
  );
};

export default SupplierAutoComplete;
